import React, { useState, FormEvent, useContext, useRef } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { get, set, cloneDeep, isFunction, isEqual } from 'lodash';
import './Form.css';
import { toggleElement } from '../../util/util';
import { handleError } from '../../model/notification';
import { Form as FormObject } from '@nerdgeschoss/react-use-form-library';

export interface FormContextType<T> {
  model: object;
  notify: <Field extends keyof T>(field: Field, value: T[Field], fullPath: string) => void;
  reportValidity: <Field extends keyof T>(field: Field, valid: boolean, fullPath: string) => void;
  triedToSubmit: boolean;
  submit: () => void;
  submissionStatus: FormObject<T>['submissionStatus'];
}

export interface Props<T> extends GlobalProps {
  model?: T;
  onSubmit?: (model: T) => void | Promise<void>;
  onInput?: (model: T, key: keyof T) => void;
  children: React.ReactNode | ((controller: FormController<T>) => JSX.Element);
  autoComplete?: boolean;
}

export function useChanges<T extends {}>(
  model: T
): [Partial<T>, (changes: Partial<T>) => void, T, (model: Partial<T>, field: keyof T) => void] {
  const [changes, setChanges] = useState<Partial<T>>({});
  const currentModel = { ...model, ...changes };
  function update(updatedModel: Partial<T>, field: keyof T): void {
    setChanges((val) => set(cloneDeep(val), field, get(updatedModel, field)));
  }
  return [changes, setChanges, currentModel, update];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FormContext = React.createContext<FormContextType<any>>({
  model: {},
  triedToSubmit: false,
  submissionStatus: 'idle',
  reportValidity: () => {},
  submit: () => {},
  notify: () => {},
});

export function useForm<T>(name: string): [T | undefined, (v: T) => void, boolean, (v: boolean) => void] {
  const context = useContext(FormContext);

  function setValue(value: T): void {
    context.notify(name, value, name);
  }

  function reportValidity(valid: boolean): void {
    context.reportValidity(name, valid, name);
  }

  return [get(context.model, name), setValue, context.triedToSubmit, reportValidity];
}

interface FormController<T> {
  valid: boolean;
  context: FormContextType<T>;
  invalidFields: (keyof T)[]; // This was for debugging purposes but it make sense to have it.
}

export function Form<T extends {}>({
  model: suppliedModel,
  onSubmit,
  onInput,
  style,
  className,
  children,
  autoComplete,
}: Props<T>): JSX.Element {
  const model = suppliedModel || ({} as T);
  const [triedToSubmit, setTriedToSubmit] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<FormObject<T>['submissionStatus']>('idle');
  const [invalidFields, setInvalidFields] = useState<Array<keyof T>>([]);
  const formRef = useRef<HTMLFormElement | null>(null);

  function notify<K extends keyof T>(field: K, value: T[K]): void {
    const newModel = cloneDeep(model);
    setSubmissionStatus('idle');
    set(newModel, field, value);
    if (onInput) {
      onInput(newModel, field);
    }
  }

  function reportValidity(field: keyof T, validity: boolean): void {
    setInvalidFields((val) => {
      const newFields = toggleElement(val, field, !validity);
      return isEqual(val, newFields) ? val : newFields;
    });
  }

  const valid = invalidFields.length === 0;

  async function submit(e?: FormEvent<HTMLFormElement>): Promise<void> {
    e?.preventDefault();
    if (onSubmit && valid) {
      const promise = onSubmit(model);
      if (promise) {
        setSubmissionStatus('submitting');
        try {
          await promise;
          setSubmissionStatus('submitted');
        } catch (error) {
          setSubmissionStatus('error');
          handleError(error);
        }
      }
    } else {
      setTriedToSubmit(true);
    }
  }

  const context: FormContextType<T> = {
    model,
    notify,
    triedToSubmit,
    reportValidity,
    submit,
    submissionStatus,
  };

  const controller: FormController<T> = { valid, invalidFields, context };

  return (
    <form
      className={classnames('form', className)}
      style={style}
      onSubmit={submit}
      noValidate
      ref={formRef}
      autoComplete={autoComplete ? undefined : 'off'}
    >
      <FormContext.Provider value={context}>
        {isFunction(children) ? children(controller) : children}
      </FormContext.Provider>
    </form>
  );
}
